import { create } from 'zustand';

import userService, { User, UpdateProfileData, UserListParams as ServiceUserListParams } from '../services/user.service';

interface UserState {
    users: User[];
    currentUser: User | null;
    total: number;
    loading: boolean;
    error: string | null;

    // Actions
    fetchUsers: (params?: ServiceUserListParams) => Promise<void>;
    fetchUser: (id: string) => Promise<void>;
    updateUser: (id: string, data: UpdateProfileData) => Promise<User>;
    deleteUser: (id: string) => Promise<void>;
    setCurrentUser: (user: User | null) => void;
    clearError: () => void;
}

export const useUserStore = create<UserState>((set) => ({
    users: [],
    currentUser: null,
    total: 0,
    loading: false,
    error: null,

    fetchUsers: async (params) => {
        set({ loading: true, error: null });
        try {
            const response = await userService.listUsers(params);
            set({
                users: response.users as any,
                total: response.total,
                loading: false,
            });
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    fetchUser: async (id) => {
        set({ loading: true, error: null });
        try {
            const user = await userService.getUserById(id);
            set({ currentUser: user as any, loading: false });
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    updateUser: async (id, data) => {
        set({ loading: true, error: null });
        try {
            const user = await userService.updateUser(id, data);
            set((state) => ({
                users: state.users.map((u) => (u._id === id ? (user as any) : u)),
                currentUser: state.currentUser?._id === id ? (user as any) : state.currentUser,
                loading: false,
            }));
            return user as any;
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    deleteUser: async (id) => {
        set({ loading: true, error: null });
        try {
            await userService.deleteUser(id);
            set((state) => ({
                users: state.users.filter((u) => u._id !== id),
                currentUser: state.currentUser?._id === id ? null : state.currentUser,
                total: state.total - 1,
                loading: false,
            }));
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    setCurrentUser: (user) => {
        set({ currentUser: user });
    },

    clearError: () => {
        set({ error: null });
    },
}));
